import { useState, useEffect, useRef } from "react";
import WaitlistForm from "../ui/WaitlistForm";
import { useScrollReveal } from "../../hooks/useScrollReveal";

// ── Reveal wrapper ────────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, direction = "up", style = {}, className = "" }) {
  const [ref, visible] = useScrollReveal(delay);
  const base =
    direction === "left"  ? "reveal-left"  :
    direction === "right" ? "reveal-right" :
    direction === "scale" ? "reveal-scale" : "reveal";
  return (
    <div ref={ref} className={`${base}${visible ? " visible" : ""} ${className}`} style={style}>
      {children}
    </div>
  );
}

// ── Count-up hook ─────────────────────────────────────────────────────────────
function useCountUp(end, { duration = 1400, decimals = 0 } = {}) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useScrollReveal();
  const started = useRef(false);

  useEffect(() => {
    if (!visible || started.current) return;
    started.current = true;
    const steps = 50;
    const increment = end / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current = Math.min(current + increment, end);
      setCount(parseFloat(current.toFixed(decimals)));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [visible, end, duration, decimals]);

  return [ref, count];
}

// ── Icons ─────────────────────────────────────────────────────────────────────
function IconSignal() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}
function IconPie() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  );
}
function IconZap() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

// ── Feature mini-UIs ──────────────────────────────────────────────────────────
function SignalsMini({ hovered }) {
  const rows = [
    { t: "AAPL", a: "BUY",  c: "#10B77F", v: "+2.4%" },
    { t: "NVDA", a: "BUY",  c: "#10B77F", v: "+5.2%" },
    { t: "META", a: "HOLD", c: "#FBBF24", v: "+0.8%" },
    { t: "TSLA", a: "BUY",  c: "#10B77F", v: "+3.1%" },
  ];
  return (
    <div style={{
      marginTop: 22, background: "rgba(0,0,0,0.28)",
      borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", padding: 14,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
        fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
        fontFamily: "Space Grotesk, sans-serif",
      }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10B77F", animation: "pulseGold 2s ease infinite" }} />
        AI Signals · Live
      </div>
      {rows.map(({ t, a, c, v }, i) => (
        <div key={t} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "5px 0",
          borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
          opacity: hovered ? 1 : 0.45,
          transform: hovered ? "translateX(0)" : `translateX(${i * 4}px)`,
          transition: `opacity 0.4s ease ${i * 0.06}s, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 0.06}s`,
        }}>
          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif", width: 42 }}>
            {t}
          </span>
          <span style={{
            fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.05em",
            padding: "2px 8px", borderRadius: 999,
            background: c + "18", border: `1px solid ${c}30`, color: c,
          }}>
            {a}
          </span>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#10B77F", fontFamily: "Space Grotesk, sans-serif" }}>
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}

function PortfolioMini({ hovered }) {
  const bars = [
    { l: "Technology", p: 42, c: "#60A5FA" },
    { l: "Crypto",     p: 28, c: "#10B77F" },
    { l: "ETFs",       p: 18, c: "#FBBF24" },
    { l: "Bonds",      p: 12, c: "#A78BFA" },
  ];
  return (
    <div style={{
      marginTop: 22, background: "rgba(0,0,0,0.28)",
      borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", padding: 14,
    }}>
      <div style={{
        fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
        fontFamily: "Space Grotesk, sans-serif", marginBottom: 12,
      }}>
        Allocation
      </div>
      {bars.map(({ l, p, c }, i) => (
        <div key={l} style={{ marginBottom: i < bars.length - 1 ? 9 : 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: "0.69rem", color: "rgba(255,255,255,0.45)", fontFamily: "Space Grotesk, sans-serif" }}>{l}</span>
            <span style={{ fontSize: "0.69rem", fontWeight: 700, color: c, fontFamily: "Space Grotesk, sans-serif" }}>{p}%</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 2,
              background: `linear-gradient(90deg, ${c}80, ${c})`,
              width: hovered ? `${p}%` : "0%",
              boxShadow: `0 0 8px ${c}60`,
              transition: `width 0.85s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`,
            }} />
          </div>
        </div>
      ))}
      <div style={{
        marginTop: 12, paddingTop: 10,
        borderTop: "1px solid rgba(255,255,255,0.05)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontSize: "0.64rem", color: "rgba(255,255,255,0.28)", fontFamily: "Space Grotesk, sans-serif" }}>
          Total Return
        </span>
        <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#10B77F", fontFamily: "Space Grotesk, sans-serif" }}>
          +18.4%
        </span>
      </div>
    </div>
  );
}

function IntelligenceMini({ hovered }) {
  const items = [
    { tag: "Earnings",  title: "NVDA beats Q1 by 18%",          time: "2m ago",  c: "#10B77F" },
    { tag: "Macro",     title: "Fed signals May rate pause",      time: "14m ago", c: "#60A5FA" },
    { tag: "Sentiment", title: "S&P 500 momentum turns bullish",  time: "1h ago",  c: "#FBBF24" },
  ];
  return (
    <div style={{
      marginTop: 22, background: "rgba(0,0,0,0.28)",
      borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", padding: 14,
    }}>
      <div style={{
        fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
        fontFamily: "Space Grotesk, sans-serif", marginBottom: 10,
      }}>
        Intelligence Feed
      </div>
      {items.map(({ tag, title, time, c }, i) => (
        <div key={title} style={{
          display: "flex", gap: 9, padding: "6px 0",
          borderBottom: i < items.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
          alignItems: "flex-start",
          opacity: hovered ? 1 : 0.45,
          transform: hovered ? "translateY(0)" : "translateY(6px)",
          transition: `opacity 0.4s ease ${i * 0.08}s, transform 0.5s ease ${i * 0.08}s`,
        }}>
          <span style={{
            fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.04em",
            padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2,
            background: c + "15", color: c, border: `1px solid ${c}28`,
          }}>
            {tag}
          </span>
          <div>
            <p style={{
              fontSize: "0.7rem", color: "rgba(240,238,246,0.65)",
              fontFamily: "Space Grotesk, sans-serif", margin: "0 0 2px", lineHeight: 1.4,
            }}>
              {title}
            </p>
            <span style={{ fontSize: "0.58rem", color: "rgba(255,255,255,0.25)", fontFamily: "Space Grotesk, sans-serif" }}>
              {time}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Feature card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon, iconBg, iconColor, label, title, desc, renderMini, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Reveal delay={delay}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: "#0C1020",
          border: `1px solid ${hovered ? "rgba(16,183,127,0.2)" : "rgba(255,255,255,0.07)"}`,
          borderRadius: 20, padding: "28px",
          height: "100%",
          transform: hovered ? "translateY(-6px)" : "translateY(0)",
          boxShadow: hovered
            ? "0 28px 72px rgba(0,0,0,0.55), 0 0 0 1px rgba(16,183,127,0.07)"
            : "0 4px 24px rgba(0,0,0,0.2)",
          transition: "border-color 0.3s ease, transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease",
        }}
      >
        {/* Icon */}
        <div style={{
          width: 44, height: 44, borderRadius: 13,
          background: iconBg,
          border: `1px solid ${iconColor}28`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: iconColor, marginBottom: 20,
          transform: hovered ? "scale(1.08) rotate(-4deg)" : "scale(1) rotate(0deg)",
          transition: "transform 0.35s cubic-bezier(0.16,1,0.3,1)",
        }}>
          {icon}
        </div>

        {/* Label */}
        <div style={{
          fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em",
          textTransform: "uppercase", color: iconColor,
          fontFamily: "Space Grotesk, sans-serif", marginBottom: 8,
        }}>
          {label}
        </div>

        {/* Title */}
        <h3 style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontSize: "1.06rem", fontWeight: 700,
          color: "#F0EEF6", lineHeight: 1.35, margin: "0 0 10px",
        }}>
          {title}
        </h3>

        {/* Desc */}
        <p style={{
          fontSize: "0.86rem", color: "rgba(240,238,246,0.45)",
          lineHeight: 1.7, fontFamily: "Space Grotesk, sans-serif", margin: 0,
        }}>
          {desc}
        </p>

        {renderMini(hovered)}
      </div>
    </Reveal>
  );
}

// ═══════════════════════════════════════════════════════════
// FEATURES SECTION
// ═══════════════════════════════════════════════════════════
export function FeaturesSection() {
  const features = [
    {
      icon: <IconSignal />,
      iconBg: "rgba(16,183,127,0.1)",
      iconColor: "#10B77F",
      label: "AI Intelligence",
      title: "Signals that see the market clearly",
      desc: "Real-time buy, hold, and sell signals across thousands of assets — ML trained on decades of price, volume, and macro data.",
      renderMini: (h) => <SignalsMini hovered={h} />,
    },
    {
      icon: <IconPie />,
      iconBg: "rgba(96,165,250,0.1)",
      iconColor: "#60A5FA",
      label: "Portfolio Analytics",
      title: "Understand your true exposure",
      desc: "Deep insight into your holdings, concentration risk, performance attribution, and sector allocation — institutional-grade tools.",
      renderMini: (h) => <PortfolioMini hovered={h} />,
    },
    {
      icon: <IconZap />,
      iconBg: "rgba(167,139,250,0.1)",
      iconColor: "#A78BFA",
      label: "Market Intelligence",
      title: "Stay ahead of the market",
      desc: "Earnings analysis, macro trends, sector rotation, and news sentiment — unified into an AI-curated feed tailored to your positions.",
      renderMini: (h) => <IntelligenceMini hovered={h} />,
    },
  ];

  return (
    <section id="features" style={{
      padding: "110px 24px",
      position: "relative",
    }}>
      {/* Separator */}
      <div style={{
        position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
        width: 480, height: 1,
        background: "linear-gradient(90deg, transparent, rgba(16,183,127,0.2), transparent)",
        pointerEvents: "none",
      }} />

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>

        {/* Header */}
        <Reveal style={{ textAlign: "center", marginBottom: 72 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>
            Platform Features
          </div>
          <h2 style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "clamp(2rem, 3.8vw, 2.9rem)",
            fontWeight: 800, letterSpacing: "-0.026em", lineHeight: 1.12,
            color: "#F0EEF6", margin: "0 0 16px",
          }}>
            Everything you need to invest smarter
          </h2>
          <p style={{
            fontSize: "1rem", color: "rgba(240,238,246,0.44)",
            lineHeight: 1.75, maxWidth: 480, margin: "0 auto",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            Three pillars that give retail investors an institutional edge
            — without the institutional price tag.
          </p>
        </Reveal>

        {/* Cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20,
        }} className="feat-grid-v3">
          {features.map((f, i) => (
            <FeatureCard key={f.title} {...f} delay={i * 120} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// STATS SECTION
// ═══════════════════════════════════════════════════════════

function StatItem({ end, suffix = "", prefix = "", label, decimals = 0 }) {
  const [ref, count] = useCountUp(end, { decimals });
  return (
    <div ref={ref} style={{ padding: "52px 28px", textAlign: "center" }}>
      <div style={{
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: "clamp(2.2rem, 3.5vw, 2.9rem)",
        fontWeight: 900, letterSpacing: "-0.04em",
        color: "#10B77F", marginBottom: 8, lineHeight: 1,
      }}>
        {prefix}{typeof count === "number" ? count.toLocaleString() : count}{suffix}
      </div>
      <div style={{
        fontSize: "0.84rem", color: "rgba(240,238,246,0.38)",
        fontFamily: "Space Grotesk, sans-serif",
      }}>
        {label}
      </div>
    </div>
  );
}

export function StatsSection() {
  return (
    <section id="stats" style={{
      borderTop:    "1px solid rgba(255,255,255,0.06)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      background: "rgba(0,0,0,0.35)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        }} className="stats-grid-v3">
          {[
            { end: 1200, suffix: "+",  label: "Investors on the waitlist" },
            { end: 4,    suffix: "",   label: "Days until launch"          },
            { end: 94.2, suffix: "%",  label: "AI signal accuracy",  decimals: 1 },
            { end: 10000,suffix: "+",  label: "Assets tracked"             },
          ].map((s, i, arr) => (
            <div key={s.label} style={{
              borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
            }} className="stat-col-v3">
              <StatItem {...s} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// CTA SECTION
// ═══════════════════════════════════════════════════════════
export function CTASection() {
  return (
    <section style={{
      padding: "130px 24px",
      position: "relative", overflow: "hidden",
    }}>
      {/* Separator */}
      <div style={{
        position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
        width: 480, height: 1,
        background: "linear-gradient(90deg, transparent, rgba(16,183,127,0.18), transparent)",
        pointerEvents: "none",
      }} />

      {/* Glow */}
      <div style={{
        position: "absolute", top: "40%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 700, height: 500, pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(16,183,127,0.07) 0%, transparent 68%)",
        filter: "blur(20px)",
      }} />

      <div style={{
        maxWidth: 660, margin: "0 auto",
        textAlign: "center", position: "relative", zIndex: 1,
      }}>
        <Reveal>
          {/* Avatar stack */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 12, marginBottom: 30,
          }}>
            <div style={{ display: "flex" }}>
              {["#10B77F","#60A5FA","#A78BFA","#FBBF24","#F87171"].map((c, i) => (
                <div key={c} style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${c}55, ${c}18)`,
                  border: "2.5px solid #06080F",
                  marginLeft: i > 0 ? -10 : 0, flexShrink: 0,
                }} />
              ))}
            </div>
            <span style={{
              fontSize: "0.82rem", color: "rgba(240,238,246,0.4)",
              fontFamily: "Space Grotesk, sans-serif",
            }}>
              <strong style={{ color: "rgba(240,238,246,0.75)" }}>1,200+</strong> investors already joined
            </span>
          </div>

          <h2 style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "clamp(1.9rem, 3.5vw, 2.7rem)",
            fontWeight: 800, letterSpacing: "-0.028em", lineHeight: 1.14,
            color: "#F0EEF6", margin: "0 0 16px",
          }}>
            Don't invest in the dark.
          </h2>

          <p style={{
            fontSize: "1rem", color: "rgba(240,238,246,0.44)",
            lineHeight: 1.75, maxWidth: 420, margin: "0 auto 42px",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            Join the waitlist and get priority access to Alpha Stack
            before our public launch.
          </p>

          <div style={{ maxWidth: 480, margin: "0 auto 18px" }}>
            <WaitlistForm variant="cta" />
          </div>

          <p style={{
            fontSize: "0.72rem", color: "rgba(240,238,246,0.22)",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            Free to join · No credit card required
          </p>
        </Reveal>
      </div>
    </section>
  );
}
